import React, { useEffect, useState } from 'react';

const VARIABLES = [
  { label: 'First Name', value: '{{first_name}}' },
  { label: 'Last Name', value: '{{last_name}}' },
  { label: 'Full Name', value: '{{first_name}} {{last_name}}' },
  { label: 'Grade', value: '{{grade}}' },
  { label: 'School', value: '{{school_name}}' },
];

// Fonts live at s3://shoobphoto/fonts/. The list is fetched from Rails
// (which reads S3) instead of hardcoded, so a fresh upload appears in
// every dropdown without a code change. See app/services/fonts_listing.rb.
// Returns { fonts, upload }. Upload throws on failure so each caller can
// scope its own uploading/error state to the field that triggered it (a
// shared spinner/error at TextTab level would flash on every field).
const useFonts = () => {
  const [fonts, setFonts] = useState([]);

  const refresh = React.useCallback(async () => {
    try {
      const res = await fetch('/admin/favorites_designer/fonts_list', {
        headers: { Accept: 'application/json' }, credentials: 'same-origin',
      });
      if (!res.ok) throw new Error(`fonts_list ${res.status}`);
      const data = await res.json();
      setFonts(data.fonts || []);
    } catch (e) {
      // Empty list is safer than crashing the editor -- the field just
      // shows "Select font..." and the pre-selected value is preserved.
      // eslint-disable-next-line no-console
      console.warn('[useFonts] refresh failed:', e.message);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const upload = React.useCallback(async (file) => {
    const form = new FormData();
    form.append('file', file);
    const res = await fetch('/admin/favorites_designer/upload_font', {
      method: 'POST', body: form, credentials: 'same-origin',
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error || `upload ${res.status}`);
    await refresh();
    return data.filename;
  }, [refresh]);

  return { fonts, upload };
};

const Field = ({ label, children, half }) => (
  <div style={{
    marginBottom: 8, display: 'inline-block', width: half ? '48%' : '100%', marginRight: half ? '4%' : 0, verticalAlign: 'top',
  }}
  >
    <label style={{
      display: 'block', fontSize: 10, color: '#666', marginBottom: 2,
    }}
    >
      {label}
    </label>
    {children}
  </div>
);

const SmallInput = ({
  value, onChange, type = 'text', placeholder, step,
}) => (
  <input
    type={type}
    value={value ?? ''}
    onChange={(e) => onChange(type === 'number' ? (e.target.value === '' ? null : Number(e.target.value)) : e.target.value)}
    onKeyDown={type === 'number' ? (e) => {
      if (e.shiftKey && (e.key === 'ArrowUp' || e.key === 'ArrowDown')) {
        e.preventDefault();
        const s = parseFloat(e.target.step) || 1;
        const current = parseFloat(e.target.value) || 0;
        const delta = (e.key === 'ArrowUp' ? 1 : -1) * s * 10;
        onChange(Math.round((current + delta) * 1e6) / 1e6);
      }
    } : undefined}
    placeholder={placeholder}
    step={step}
    style={{
      width: '100%',
      padding: '4px 6px',
      border: '1px solid #ddd',
      borderRadius: 3,
      fontSize: 12,
      boxSizing: 'border-box',
    }}
  />
);

// Displays a decimal (0.12) as percentage (12.00) and converts back on change
const PctInput = ({ value, onChange, placeholder }) => {
  const displayVal = value != null ? Math.round(value * 10000) / 100 : '';
  return (
    <input
      type="number"
      value={displayVal === '' ? '' : displayVal}
      onChange={(e) => {
        if (e.target.value === '') { onChange(null); return; }
        onChange(Math.round(Number(e.target.value) * 100) / 10000);
      }}
      onKeyDown={(e) => {
        if (e.shiftKey && (e.key === 'ArrowUp' || e.key === 'ArrowDown')) {
          e.preventDefault();
          const current = parseFloat(e.target.value) || 0;
          const delta = (e.key === 'ArrowUp' ? 1 : -1);
          onChange(Math.round((current + delta) * 100) / 10000);
        }
      }}
      placeholder={placeholder}
      step="0.1"
      style={{
        width: '100%',
        padding: '4px 6px',
        border: '1px solid #ddd',
        borderRadius: 3,
        fontSize: 12,
        boxSizing: 'border-box',
      }}
    />
  );
};

const TextFieldEditor = ({
  field, index, onUpdate, onRemove, fonts, uploadFont,
}) => {
  const [expanded, setExpanded] = useState(true);
  // Per-field upload state -- shared state would mean Field 1's "Uploading…"
  // disables every other field's button and its error renders under every
  // field.
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState(null);
  const fileRef = React.useRef(null);

  const onFilePicked = async (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    setUploadError(null);
    setUploading(true);
    try {
      const filename = await uploadFont(file);
      // Reset the input so re-picking the same file re-fires onChange.
      e.target.value = '';
      // Auto-select the just-uploaded font for THIS field so the designer
      // sees it applied without touching the dropdown.
      if (filename) onUpdate({ font_filename: filename });
      else setUploadError('Upload failed');
    } catch (err) {
      setUploadError(err.message || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div style={{
      marginBottom: 12,
      border: '1px solid #e0e0e0',
      borderRadius: 4,
      background: '#fff',
      overflow: 'hidden',
    }}
    >
      <div
        onClick={() => setExpanded(!expanded)}
        style={{
          padding: '8px 12px',
          background: '#f5f5f5',
          cursor: 'pointer',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <span style={{ fontSize: 13, fontWeight: 500 }}>
          Field
          {' '}
          {index + 1}
          :
          {' '}
          {field.content || '(empty)'}
        </span>
        <span style={{ fontSize: 11, color: '#999' }}>{expanded ? '▼' : '▶'}</span>
      </div>

      {expanded && (
        <div style={{ padding: 12 }}>
          <Field label="Content">
            <SmallInput value={field.content} onChange={(v) => onUpdate({ content: v })} placeholder="{{first_name}}" />
          </Field>

          <div style={{ marginBottom: 8 }}>
            <span style={{ fontSize: 10, color: '#666' }}>Insert variable: </span>
            {VARIABLES.map((v) => (
              <button
                key={v.value}
                onClick={() => onUpdate({ content: (field.content || '') + v.value })}
                style={{
                  fontSize: 10,
                  padding: '2px 6px',
                  marginRight: 4,
                  marginTop: 2,
                  background: '#e3f2fd',
                  border: '1px solid #90caf9',
                  borderRadius: 3,
                  cursor: 'pointer',
                }}
              >
                {v.label}
              </button>
            ))}
          </div>

          <Field label="Font">
            <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
              <select
                value={field.font_filename || ''}
                onChange={(e) => onUpdate({ font_filename: e.target.value })}
                style={{
                  flex: 1,
                  padding: '4px 6px',
                  border: '1px solid #ddd',
                  borderRadius: 3,
                  fontSize: 12,
                  background: '#fff',
                  boxSizing: 'border-box',
                }}
              >
                <option value="">Select font...</option>
                {/* If the field references a font not in the current list (e.g.
                    the font was renamed on S3), keep it selectable so we don't
                    silently drop the designer's choice. */}
                {field.font_filename && !fonts.includes(field.font_filename) && (
                  <option value={field.font_filename}>
                    {field.font_filename}
                    {' '}
                    (not found on S3)
                  </option>
                )}
                {fonts.map((f) => <option key={f} value={f}>{f}</option>)}
              </select>
              <input
                ref={fileRef}
                type="file"
                accept=".ttf,.otf,.woff,.woff2,font/ttf,font/otf,font/woff,font/woff2,application/x-font-ttf,application/x-font-otf"
                onChange={onFilePicked}
                style={{ display: 'none' }}
              />
              <button
                type="button"
                onClick={() => fileRef.current && fileRef.current.click()}
                disabled={uploading}
                title="Upload a new font to s3://shoobphoto/fonts/"
                style={{
                  padding: '4px 8px',
                  fontSize: 11,
                  background: uploading ? '#eee' : '#fff',
                  color: '#333',
                  border: '1px solid #ccc',
                  borderRadius: 3,
                  cursor: uploading ? 'wait' : 'pointer',
                  whiteSpace: 'nowrap',
                }}
              >
                {uploading ? 'Uploading…' : '+ Upload'}
              </button>
            </div>
            {uploadError && (
              <div style={{ fontSize: 10, color: '#c62828', marginTop: 4 }}>{uploadError}</div>
            )}
          </Field>

          <Field label="Font S3 Path">
            <SmallInput value={field.font_s3_path} onChange={(v) => onUpdate({ font_s3_path: v })} placeholder="s3://shoobphoto/fonts/" />
          </Field>

          <Field label="Color" half>
            <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
              <input
                type="color"
                value={field.font_color || '#000000'}
                onChange={(e) => onUpdate({ font_color: e.target.value })}
                style={{
                  width: 28, height: 28, border: 'none', cursor: 'pointer',
                }}
              />
              <SmallInput value={field.font_color} onChange={(v) => onUpdate({ font_color: v })} />
            </div>
          </Field>

          <Field label="Size Min (pt)" half>
            <SmallInput type="number" value={field.font_size_min} onChange={(v) => onUpdate({ font_size_min: v })} />
          </Field>
          <Field label="Size Max (pt)" half>
            <SmallInput type="number" value={field.font_size_max} onChange={(v) => onUpdate({ font_size_max: v })} />
          </Field>

          <Field label="Center X (%)" half>
            <PctInput value={field.center_x} onChange={(v) => onUpdate({ center_x: v })} placeholder="e.g. 50" />
          </Field>
          <Field label="Center Y (%)" half>
            <PctInput value={field.center_y} onChange={(v) => onUpdate({ center_y: v })} placeholder="e.g. 85" />
          </Field>

          <Field label="Bound Left (%)" half>
            <PctInput value={field.bound_x_left} onChange={(v) => onUpdate({ bound_x_left: v })} placeholder="e.g. 4" />
          </Field>
          <Field label="Bound Right (%)" half>
            <PctInput value={field.bound_x_right} onChange={(v) => onUpdate({ bound_x_right: v })} placeholder="e.g. 96" />
          </Field>

          <Field label="Sizing Rule">
            <select
              value={field.sizing_rule || 'shrink_to_fit'}
              onChange={(e) => onUpdate({ sizing_rule: e.target.value })}
              style={{
                width: '100%',
                padding: '4px 6px',
                border: '1px solid #ddd',
                borderRadius: 3,
                fontSize: 12,
                background: '#fff',
                boxSizing: 'border-box',
              }}
            >
              <option value="shrink_to_fit">Shrink to fit</option>
              <option value="fixed">Fixed size</option>
              <option value="wrap">Wrap text</option>
              <option value="fit_to_bounds_max_first">Fit to bounds (max first)</option>
              <option value="single_line_or_wrap">One line if it fits, wrap if forced</option>
            </select>
          </Field>

          <Field label="Line Spacing (blank = rule default)">
            {/* Blank = use the rule's default line-height (0.9x for
                single_line_or_wrap, 1.2x for everything else). Explicit
                numeric value overrides -- e.g. 0.85 for tighter cap
                overlap, 1.4 for a looser stack. Preview + EC2 read the
                same field so what you see matches what renders. */}
            <SmallInput
              type="number"
              step="0.05"
              value={field.line_spacing_multiplier}
              onChange={(v) => onUpdate({ line_spacing_multiplier: v })}
              placeholder="e.g. 0.85"
            />
          </Field>

          <Field label="Align">
            <select
              value={field.align || 'center'}
              onChange={(e) => onUpdate({ align: e.target.value })}
              style={{
                width: '100%',
                padding: '4px 6px',
                border: '1px solid #ddd',
                borderRadius: 3,
                fontSize: 12,
                background: '#fff',
                boxSizing: 'border-box',
              }}
            >
              <option value="left">Left</option>
              <option value="center">Center</option>
              <option value="right">Right</option>
            </select>
          </Field>

          <div style={{ display: 'flex', gap: 12, marginTop: 4 }}>
            <label style={{
              fontSize: 11, display: 'flex', alignItems: 'center', gap: 4,
            }}
            >
              <input type="checkbox" checked={field.bold || false} onChange={(e) => onUpdate({ bold: e.target.checked })} />
              Bold
            </label>
            <label style={{
              fontSize: 11, display: 'flex', alignItems: 'center', gap: 4,
            }}
            >
              <input type="checkbox" checked={field.italic || false} onChange={(e) => onUpdate({ italic: e.target.checked })} />
              Italic
            </label>
            <label style={{
              fontSize: 11, display: 'flex', alignItems: 'center', gap: 4,
            }}
            >
              <input type="checkbox" checked={field.shadow || false} onChange={(e) => onUpdate({ shadow: e.target.checked })} />
              Shadow
            </label>
          </div>

          <button
            onClick={onRemove}
            style={{
              marginTop: 8,
              padding: '4px 12px',
              fontSize: 11,
              color: '#d32f2f',
              background: '#ffebee',
              border: '1px solid #ef9a9a',
              borderRadius: 3,
              cursor: 'pointer',
            }}
          >
            Remove Field
          </button>
        </div>
      )}
    </div>
  );
};

const TextTab = ({
  textFields, onUpdate, onAdd, onRemove,
}) => {
  const {
    fonts, upload: uploadFont,
  } = useFonts();
  return (
    <div>
      <div style={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12,
      }}
      >
        <h4 style={{ margin: 0, fontSize: 14, color: '#333' }}>Text Fields</h4>
        <button
          onClick={onAdd}
          style={{
            padding: '4px 12px',
            fontSize: 12,
            background: '#1976d2',
            color: '#fff',
            border: 'none',
            borderRadius: 3,
            cursor: 'pointer',
          }}
        >
          + Add Field
        </button>
      </div>

      {textFields.length === 0 && (
        <div style={{
          padding: 20, textAlign: 'center', color: '#999', fontSize: 13,
        }}
        >
          No text fields. Click "+ Add Field" to add one.
        </div>
      )}

      {textFields.map((field, i) => (
        <TextFieldEditor
          key={field.id || `new-${i}`}
          field={field}
          index={i}
          onUpdate={(changes) => onUpdate(i, changes)}
          onRemove={() => onRemove(i)}
          fonts={fonts}
          uploadFont={uploadFont}
        />
      ))}
    </div>
  );
};

export default TextTab;
